<template>
  <v-app>
    <AppNavigation v-model="drawer" />

    <v-app-bar class="custom-header" elevation="0">
      <v-app-bar-nav-icon :aria-label="t('layout.open_menu')" @click="drawer = !drawer" />
      <!-- Logo compacto en móvil -->
      <RouterLink
        v-show="isMobile"
        :to="{ name: 'home' }"
        class="header-logo header-logo--mobile"
        :aria-label="t('layout.logo_home')"
      >
        <img src="/logo/watchstreak-logo.svg" alt="" class="header-logo__icon" aria-hidden="true" />
        <span class="header-logo__title header-logo__title--mobile">
          Watch<span class="header-logo__title-accent">Streak</span>
        </span>
      </RouterLink>
      <!-- Logo completo en escritorio -->
      <div v-show="!isMobile" class="header-logo">
        <img src="/logo/watchstreak-logo.svg" alt="" class="header-logo__icon" aria-hidden="true" />
        <div class="d-flex flex-column justify-center">
          <v-toolbar-title class="header-logo__title">
            Watch<span class="header-logo__title-accent">Streak</span>
          </v-toolbar-title>
          <span class="header-logo__tagline">{{ t('layout.tagline') }}</span>
        </div>
      </div>
      <v-spacer />
      
      <div class="header-actions">
        <!-- Búsqueda global (modal) -->
        <v-btn
          icon
          variant="text"
          class="header-action-btn"
          :aria-label="t('layout.search')"
          @click="searchMenuOpen = true"
        >
          <v-icon>mdi-magnify</v-icon>
        </v-btn>
        <GlobalSearchModal
          v-model="searchMenuOpen"
          @select="handleSearchSelect"
        />
        
        <!-- Botón de notificaciones -->
        <v-btn
          v-if="isAuthenticated"
          :to="{ name: 'notifications' }"
          icon
          variant="text"
          class="header-action-btn notification-btn"
          :aria-label="t('nav.notifications')"
        >
          <span class="notification-btn-inner">
            <v-icon>{{ hasUnreadNotifications ? 'mdi-bell' : 'mdi-bell-outline' }}</v-icon>
            <span v-if="hasUnreadNotifications" class="notification-dot notification-dot--pulse" aria-hidden="true" />
          </span>
        </v-btn>
        
        <!-- Botón de login o información de usuario -->
        <div class="header-action-btn header-user-wrap">
        <v-btn
          v-if="!isAuthenticated && isMobile"
          @click="showLoginModal = true"
          icon
          variant="flat"
          class="btn-gradient-primary login-btn-icon"
          :title="t('layout.login')"
          :aria-label="t('layout.login')"
        >
          <v-icon>mdi-power</v-icon>
        </v-btn>
        <v-btn
          v-else-if="!isAuthenticated"
          @click="showLoginModal = true"
          class="btn-gradient-primary login-btn"
        >
          {{ t('layout.login') }}
        </v-btn>
        <v-menu v-else location="bottom end" :offset="8" :close-on-content-click="false">
          <template v-slot:activator="{ props }">
            <button
              v-bind="props"
              type="button"
              class="user-menu-activator"
              :aria-label="t('layout.user_menu')"
              aria-haspopup="menu"
            >
              <span v-if="!isMobile" class="user-name">{{ user?.name || t('layout.user_fallback') }}</span>
              <v-avatar size="36" class="user-avatar">
                <v-img
                  v-if="userAvatarUrl"
                  :src="userAvatarUrl"
                  cover
                  :alt="t('layout.avatar_alt')"
                />
                <v-icon v-else icon="mdi-account-circle" size="36" aria-hidden="true" />
              </v-avatar>
            </button>
          </template>
          <v-card min-width="240" elevation="4" class="user-menu-card">
            <v-list density="comfortable" class="pa-0">
              <v-list-item class="px-4 py-3 user-menu-header">
                <template v-slot:prepend>
                  <v-avatar size="48" class="mr-3">
                    <v-img
                      v-if="userAvatarUrl"
                      :src="userAvatarUrl"
                      cover
                      :alt="t('layout.avatar_alt')"
                    />
                    <v-icon v-else icon="mdi-account-circle-outline" size="48" aria-hidden="true" />
                  </v-avatar>
                </template>
                <v-list-item-title class="user-menu-header__name">
                  {{ user?.name || t('layout.user_fallback') }}
                </v-list-item-title>
                <v-list-item-subtitle class="user-menu-header__email">
                  {{ user?.email }}
                </v-list-item-subtitle>
              </v-list-item>
              <v-divider />
              <v-list-item
                :to="{ name: 'profile' }"
                :title="t('layout.my_profile')"
                class="px-4 user-menu-item"
              >
                <template #prepend>
                  <v-icon icon="mdi-account-outline" size="24" />
                </template>
              </v-list-item>
              <v-divider />
              <v-list-item
                :title="t('layout.logout')"
                class="px-4 user-menu-item user-menu-logout"
                @click="handleLogout"
              >
                <template #prepend>
                  <v-icon icon="mdi-logout-variant" size="24" />
                </template>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
        </div>
      </div>
    </v-app-bar>

    <!-- Toasts de logros desbloqueados (arriba a la izquierda, debajo del header) -->
    <div v-if="isAuthenticated && achievementToastsStore.items.length > 0" class="achievement-toasts">
      <TransitionGroup name="achievement-toast">
        <div
          v-for="toast in achievementToastsStore.items"
          :key="toast.id"
          class="achievement-toast"
          role="alert"
        >
          <div class="achievement-toast__icon">
            <v-icon :icon="toast.icon" size="28" />
          </div>
          <div class="achievement-toast__body">
            <p class="achievement-toast__title">{{ toast.name }}</p>
            <p v-if="toast.description" class="achievement-toast__desc">{{ toast.description }}</p>
            <router-link :to="{ name: 'achievements' }" class="achievement-toast__link" @click="achievementToastsStore.remove(toast.id)">
              {{ t('layout.view_achievements') }}
            </router-link>
          </div>
          <v-btn
            icon
            variant="text"
            size="x-small"
            class="achievement-toast__close"
            :aria-label="t('actions.close')"
            @click="achievementToastsStore.remove(toast.id)"
          >
            <v-icon icon="mdi-close" size="18" />
          </v-btn>
        </div>
      </TransitionGroup>
    </div>

    <v-main>
      <div :class="['main-content', `main-content--${layoutMode}`]">
        <slot />
      </div>
    </v-main>

    <v-footer class="app-footer">
      <div class="footer-row text-medium-emphasis">
        <span class="footer-left">
          <span class="footer-copy">© {{ year }} WatchStreak</span>
          <span class="footer-sep" aria-hidden="true">|</span>
          <RouterLink :to="{ name: 'privacy-policy' }" class="footer-legal-link">{{ t('footer.privacy') }}</RouterLink>
          <span class="footer-sep" aria-hidden="true">|</span>
          <RouterLink :to="{ name: 'cookie-policy' }" class="footer-legal-link">{{ t('footer.cookies') }}</RouterLink>
        </span>
        <span v-if="version" class="footer-version">{{ version }}</span>
      </div>
    </v-footer>

    <!-- Banner de consentimiento de cookies -->
    <CookieConsentBanner />

    <!-- Modal de Login -->
    <LoginModal v-model="showLoginModal" @success="handleLoginSuccess" />

    <!-- Snackbar global -->
    <GlobalSnackbar />

    <!-- Única confirmación de la app: las páginas la abren con confirm() -->
    <AppConfirmHost />

    <!-- Degradados SVG con nombre, usados por los iconos de cabecera -->
    <GradientDefs />

    <!-- Cards de notificación en tiempo real (esquina superior derecha) -->
    <NotificationCards />

    <!-- FAB global: navega a añadir contenido -->
    <AddFab
      v-if="canCreate"
      :aria-label="t('layout.add_content')"
      @click="goToAddContent"
    />
  </v-app>
</template>

<script setup>
import { ref, onMounted, computed, provide, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useBreakpoints } from '@/composables/useBreakpoints'
import { useAuthStore } from '@/stores/auth'
import { useNotificationCardsStore } from '@/stores/notificationCards'
import { useSnackbarStore } from '@/stores/snackbar'
import { useAchievementToastsStore } from '@/stores/achievementToasts'
import LoginModal from '@/components/auth/LoginModal.vue'
import AppNavigation from '@/layouts/AppNavigation.vue'
import GlobalSnackbar from '@/components/shared/GlobalSnackbar.vue'
import AppConfirmHost from '@/components/ui/AppConfirmHost.vue'
import GradientDefs from '@/components/ui/GradientDefs.vue'
import NotificationCards from '@/components/shared/NotificationCards.vue'
import AddFab from '@/components/shared/AddFab.vue'
import CookieConsentBanner from '@/components/CookieConsentBanner.vue'
import GlobalSearchModal from '@/components/shared/GlobalSearchModal.vue'
import { apiFetch } from '@/utils/api'
import { getImageUrl } from '@/utils/images'
import { getEcho } from '@/plugins/echo'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { isMobile } = useBreakpoints()

const drawer = ref(false)
const searchMenuOpen = ref(false)
const year = new Date().getFullYear()
const version = ref('')
const showLoginModal = ref(false)
provide('openLoginModal', () => {
  showLoginModal.value = true
})

const authStore = useAuthStore()
const snackbarStore = useSnackbarStore()
const notificationCardsStore = useNotificationCardsStore()
const achievementToastsStore = useAchievementToastsStore()

const user = computed(() => authStore.user)
const isAuthenticated = computed(() => authStore.isAuthenticated)
const canCreate = computed(() =>
  authStore.hasPermission('create tvshows') ||
  authStore.hasPermission('create movies') ||
  authStore.hasPermission('create shows')
)

// El ancho del contenido lo decide la ruta (meta.layout), no una lista de
// nombres de página dentro del layout.
const layoutMode = computed(() => route.meta?.layout || 'default')

const unreadNotificationsCount = ref(0)
const hasUnreadNotifications = computed(() => unreadNotificationsCount.value > 0)

function goToAddContent() {
  const query = {}
  if (route.name === 'shows') query.tab = 'espectaculo'
  else if (route.name === 'movies') query.tab = 'pelicula'
  else if (route.name === 'tvshows') query.tab = 'serie'
  router.push({ name: 'add-content', query: Object.keys(query).length ? query : undefined })
}

function handleSearchSelect(item) {
  if (!item?.type || !item?.id) return
  const name =
    item.type === 'movie'
      ? 'movie-detail'
      : item.type === 'tvshow'
        ? 'tvshow-detail'
        : item.type === 'show'
          ? 'show-detail'
          : null
  if (name) router.push({ name, params: { id: item.id } })
  searchMenuOpen.value = false
}

const userAvatarUrl = computed(() => {
  if (user.value?.avatar) {
    // Forzar miniatura para evitar descargar el original (varios MB) en header/menú
    return getImageUrl(user.value.avatar, true, false, 'thumb')
  }
  return null
})

async function loadUnreadNotificationsCount() {
  if (!isAuthenticated.value) {
    unreadNotificationsCount.value = 0
    return
  }
  
  try {
    const res = await apiFetch('/api/v1/notifications/unread')
    if (res.ok) {
      const payload = await res.json()
      unreadNotificationsCount.value = payload.data?.length || 0
    }
  } catch (error) {
    console.warn('Error loading unread notifications count:', error)
  }
}

// Al salir de la página de notificaciones, refrescar el contador (p. ej. tras "marcar todo como leído")
watch(
  () => route.name,
  (name, prevName) => {
    if (prevName === 'notifications' && name !== 'notifications' && isAuthenticated.value) {
      loadUnreadNotificationsCount()
    }
  }
)

// Tras verificar email, el backend redirige aquí con ?verified=1 y sesión iniciada; sincronizamos usuario y mostramos mensaje
watch(
  () => route.query?.verified,
  async (verified) => {
    if (verified === undefined) return
    if (verified === '1') {
      const ok = await authStore.fetchUserFromApi()
      if (ok) {
        snackbarStore.success(t('toast.account_activated'))
      }
      router.replace({ path: route.path, query: {} }).catch(() => {})
      return
    }
    if (verified === '0') {
      const errorMsg = route.query?.error || t('toast.verification_link_invalid')
      snackbarStore.error(errorMsg)
      router.replace({ path: route.path, query: {} }).catch(() => {})
    }
  },
  { immediate: true }
)

onMounted(async () => {
  // Cargar versión
  try {
    const response = await fetch('/version.txt')
    if (response.ok) {
      const text = await response.text()
      version.value = text.trim()
    }
  } catch (error) {
    console.warn('No se pudo cargar la versión:', error)
  }

  // Cargar contador de notificaciones no leídas al montar
  await loadUnreadNotificationsCount()

  // Suscripción a notificaciones en tiempo real (Reverb)
  if (isAuthenticated.value && user.value?.id) {
    const echo = getEcho()
    if (!echo) {
      console.warn(
        '[Reverb] No se pudo conectar: faltan VITE_REVERB_APP_KEY, VITE_REVERB_HOST, etc. en el .env del frontend.'
      )
    } else {
      // Echo.private() ya añade el prefijo "private-"; el backend usa canal "App.Models.User.{id}"
      const channelName = `App.Models.User.${user.value.id}`
      echo.private(channelName)
        .listen('.NotificationSent', (e) => {
          if (import.meta.env.DEV) console.log('[Reverb] NotificationSent recibido:', e)
          // Reverb/Pusher puede enviar el payload como string JSON en e, en e.data, o como objeto
          const raw = e?.payload ?? e?.data ?? e
          let data = typeof raw === 'string' ? (() => { try { return JSON.parse(raw) } catch { return {} } })() : (raw ?? {})
          if (!data || typeof data !== 'object') data = {}
          // Si el objeto tiene ya los campos del broadcast, usarlo; si no, puede estar anidado
          if (data.title === undefined && data.unread_count === undefined && data?.data) {
            const inner = typeof data.data === 'string' ? (() => { try { return JSON.parse(data.data) } catch { return {} } })() : data.data
            data = inner || data
          }
          const newCount = data?.unread_count !== undefined ? data.unread_count : unreadNotificationsCount.value + 1
          const title = data?.title || t('notificationsPage.new_notification')
          nextTick(() => {
            unreadNotificationsCount.value = newCount
            notificationCardsStore.add({
              title,
              message: data?.message ?? '',
              actionRoute: { name: 'notifications' },
              actionText: t('notificationsPage.view'),
              duration: 6000,
            })
          })
        })
    }
  }
})

// Auto-ocultar toasts de logros tras 5 segundos
watch(() => achievementToastsStore.items.length, (len, prevLen) => {
  if (len <= prevLen) return
  const added = achievementToastsStore.items.slice(prevLen)
  added.forEach((t) => {
    setTimeout(() => achievementToastsStore.remove(t.id), 5000)
  })
})

function handleLoginSuccess() {
  // El usuario ya se guardó en localStorage durante el login
  // Solo cerramos el modal
  showLoginModal.value = false
}

async function handleLogout() {
  await authStore.logout()
  // Redirigir al home después del logout
  window.location.href = '/'
}
</script>

<style scoped>
/* El ancho lo marca meta.layout de la ruta. Las páginas ya migradas usan
   además <PageContainer> para su propio ancho de lectura. */
.main-content {
  width: 100%;
}

.main-content--default {
  max-width: var(--ws-container-max);
  margin: 0 auto;
  padding: var(--ws-space-6) var(--ws-space-4);
}

.main-content--reading {
  max-width: var(--ws-container-reading);
  margin: 0 auto;
  padding: var(--ws-space-6) var(--ws-space-4);
}

/* A sangre: la página gestiona su propio ancho (heros, landing, reviews).
   Sin padding, para que un hero pueda pegarse a la cabecera. */
.main-content--wide {
  padding: 0;
}

/* Hueco inferior para que el botón flotante no tape el final del contenido. */
.main-content {
  padding-bottom: var(--ws-space-16);
}

@media (max-width: 600px) {
  .main-content--default,
  .main-content--reading {
    padding: var(--ws-space-4) var(--ws-space-3) var(--ws-space-16);
  }
}

/* El footer es una línea de cortesía: altura acotada para que no se coma
   pantalla y respire por dentro en vez de apelotonarse contra los bordes. */
.app-footer {
  min-height: 48px;
  max-height: 56px;
  padding: var(--ws-space-2) var(--ws-space-6);
  border-top: 1px solid var(--ws-border-subtle);
}
.footer-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ws-space-4);
  font-size: var(--ws-text-xs);
  line-height: 1.5;
}
.footer-left {
  display: flex;
  align-items: center;
  gap: var(--ws-space-3);
  min-width: 0;
}
.footer-copy,
.footer-legal-link {
  white-space: nowrap;
}
.footer-sep {
  color: rgba(255, 255, 255, 0.25);
}
.footer-legal-link {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
}
.footer-legal-link:hover {
  color: rgba(255, 255, 255, 0.95);
  text-decoration: underline;
}
.footer-version {
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
  margin-left: auto;
}

@media (max-width: 700px) {
  /* En móvil los textos legales no caben en una línea: dejamos que envuelvan
     y subimos el techo lo justo para dos líneas. */
  .app-footer {
    max-height: 84px;
    padding: var(--ws-space-2) var(--ws-space-4);
  }
  .footer-row {
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--ws-space-1) var(--ws-space-3);
  }
  .footer-left {
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--ws-space-2);
  }
  .footer-version {
    margin-left: 0;
  }
}

/* Header y drawer: mismo fondo que las páginas, casi negro */
.custom-header {
  background: rgb(18, 18, 18) !important;
  color: #fff !important;
  /* Sombra en vez de borde: el borde sumaba 1px al alto de la barra y el
     contenido se colaba por debajo, porque v-main reserva solo su altura. */
  box-shadow: inset 0 -1px 0 var(--ws-border-subtle) !important;
}

.custom-header :deep(.v-btn),
.custom-header :deep(.v-icon),
.custom-header :deep(.v-app-bar-nav-icon) {
  color: rgba(255, 255, 255, 0.85) !important;
}

.custom-header :deep(.v-btn:hover) {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

/* Grupo buscador + notificaciones + login/avatar: más juntos y separados del borde derecho */
.header-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-right: 12px;
}
.header-action-btn {
  margin-left: 0 !important;
  margin-right: 0 !important;
}
.header-user-wrap {
  margin-left: 0;
}
.header-user-wrap .login-btn-icon {
  margin-left: 0;
}
/* Misma separación entre notificaciones y avatar que entre el resto de iconos */
.header-actions .user-menu-activator {
  margin-left: 0;
  padding-left: 0;
  padding-right: 4px;
}

/* Logo cabecera: el SVG ya trae su cuadrado redondeado con degradado, así que
   aquí solo mandamos el tamaño. El texto va en dos tonos: "Watch" en blanco y
   "Streak" en el rosa de marca. */
.header-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 4px;
}

.header-logo__icon {
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 12px;
  display: block;
  flex-shrink: 0;
}

.header-logo__title {
  font-size: 1.25rem !important;
  font-weight: 700 !important;
  color: #fff !important;
  line-height: 1.2 !important;
  padding: 0 !important;
  letter-spacing: -0.01em;
}

.header-logo__title-accent {
  color: var(--ws-brand-pink-300);
}

/* Logo compacto en móvil: enlace al inicio */
a.header-logo {
  text-decoration: none;
  color: inherit;
}
.header-logo--mobile .header-logo__icon {
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
}
.header-logo__title--mobile {
  font-size: 1rem !important;
  font-weight: 700 !important;
  color: #fff !important;
  white-space: nowrap;
}

.header-logo__tagline {
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--ws-brand-purple-300);
  line-height: 1.2;
  margin-top: 2px;
}

/* Mismo estilo que el botón de la modal: degradado y texto vía .btn-gradient-primary */
.login-btn {
  font-size: 14px !important;
}

/* En móvil: botón circular de login con degradado (mismo que formularios) */
.login-btn-icon {
  margin-left: 4px;
  min-width: 40px !important;
  width: 40px !important;
  height: 40px !important;
  padding: 0 !important;
}
.login-btn-icon :deep(.v-icon) {
  color: #fff !important;
}
.login-btn-icon :deep(.v-btn__overlay) {
  background: transparent !important;
}
.login-btn-icon:hover :deep(.v-btn__overlay) {
  background: rgba(255, 255, 255, 0.15) !important;
}

.user-menu-activator {
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 8px;
  margin-left: 8px;
  border-radius: 24px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.user-menu-activator:hover {
  background-color: rgba(25, 60, 184, 0.25);
}

.user-menu-activator:focus-visible {
  outline: 2px solid var(--ws-brand-pink-400);
  outline-offset: 2px;
}

.user-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85) !important;
  font-size: 0.9375rem;
}

.user-avatar {
  border: 2px solid rgba(var(--v-theme-on-surface), 0.12);
  transition: border-color 0.2s ease;
}

.user-menu-activator:hover .user-avatar {
  border-color: rgba(var(--v-theme-primary), 0.4);
}

:deep(.user-menu-card) {
  border-radius: 16px;
  overflow: hidden;
  background: #1e1e1e !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

:deep(.user-menu-card .v-divider) {
  border-color: rgba(255, 255, 255, 0.12);
}

:deep(.user-menu-card .user-menu-header .user-menu-header__name) {
  color: #ffffff !important;
  font-size: 0.875rem !important;
  font-weight: 500 !important;
}

:deep(.user-menu-card .user-menu-header .user-menu-header__email) {
  color: #aaaaaa !important;
  font-size: 0.75rem !important;
}

:deep(.user-menu-card .v-list-item) {
  min-height: 40px;
  border-radius: 8px;
  margin: 2px 8px;
  color: #ffffff;
}

:deep(.user-menu-card .user-menu-item .v-list-item-title),
:deep(.user-menu-card .user-menu-item .v-icon) {
  font-size: 0.8125rem !important;
  color: inherit;
}

:deep(.user-menu-card .v-list-item .v-list-item-title),
:deep(.user-menu-card .v-list-item .v-icon) {
  color: inherit;
}

:deep(.user-menu-card .v-list-item:hover) {
  background-color: rgba(255, 255, 255, 0.06);
}

:deep(.user-menu-card .user-menu-logout),
:deep(.user-menu-card .user-menu-logout .v-list-item-title),
:deep(.user-menu-card .user-menu-logout .v-icon) {
  color: #ff4d4d !important;
}

:deep(.user-menu-card .user-menu-logout:hover) {
  background-color: rgba(255, 77, 77, 0.15) !important;
}

/* Toasts de logros: arriba a la derecha, debajo del header */
.achievement-toasts {
  position: fixed;
  top: 88px;
  right: 16px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 360px;
}

.achievement-toast {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.98) 0%, rgba(26, 18, 37, 0.98) 100%);
  border: 1px solid rgba(142, 45, 226, 0.4);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

.achievement-toast__icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(241, 176, 0, 0.3) 0%, rgba(255, 152, 0, 0.25) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(241, 176, 0);
}

.achievement-toast__body {
  flex: 1;
  min-width: 0;
}

.achievement-toast__title {
  font-weight: 600;
  font-size: 0.9375rem;
  margin: 0 0 4px 0;
  color: rgba(255, 255, 255, 0.95);
}

.achievement-toast__desc {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 6px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.achievement-toast__link {
  font-size: 0.8125rem;
  color: rgb(241, 176, 0);
  text-decoration: none;
  font-weight: 500;
}

.achievement-toast__link:hover {
  text-decoration: underline;
}

.achievement-toast__close {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.5) !important;
}

.achievement-toast-enter-active,
.achievement-toast-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.achievement-toast-enter-from,
.achievement-toast-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

/* Punto rojo pulsante para notificaciones sin leer (muy visible) */
.notification-btn-inner {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.notification-dot {
  position: absolute;
  top: 0;
  right: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #e53935;
  box-shadow: 0 0 0 2px rgba(30, 30, 30, 0.95);
  pointer-events: none;
  z-index: 2;
}

.notification-dot--pulse {
  animation: notification-pulse 1.5s ease-in-out infinite;
}

@keyframes notification-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 0 2px rgba(30, 30, 30, 0.95);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.2);
    box-shadow: 0 0 0 2px rgba(30, 30, 30, 0.95), 0 0 8px rgba(229, 57, 53, 0.6);
  }
}

/* En el drawer, el punto va al lado del ítem */
</style>

